import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { UserCircleGear, Plus, PencilSimple, Trash, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ username: "", password: "", name: "", role: "staff", brand_name: "" });
  const [editData, setEditData] = useState({ name: "", password: "", brand_name: "", is_active: true });

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API}/users`);
      setUsers(res.data.users);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, formData);
      toast.success("User created successfully");
      setShowAddDialog(false);
      setFormData({ username: "", password: "", name: "", role: "staff", brand_name: "" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (editData.name) payload.name = editData.name;
      if (editData.password) payload.password = editData.password;
      if (editData.brand_name !== undefined) payload.brand_name = editData.brand_name;
      if (editData.is_active !== undefined) payload.is_active = editData.is_active;
      await axios.put(`${API}/users/${editUser.id}`, payload);
      toast.success("User updated");
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditData({ name: u.name, password: "", brand_name: u.brand_name || "", is_active: u.is_active });
    setShowEditDialog(true);
  };

  const roleBadge = { admin: "bg-red-100 text-red-700", staff: "bg-blue-100 text-blue-700", brand: "bg-amber-100 text-amber-700" };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">User Management</h2>
          <p className="text-[#64748B] text-sm mt-1">Manage admin, staff, and brand logins</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#002FA7] hover:bg-[#001F6E] gap-2" data-testid="add-user-btn"><Plus size={18} />Add User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Create New User</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Username *</Label>
                <Input placeholder="e.g., dominos_brand" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required data-testid="new-user-username" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Password *</Label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} placeholder="Set password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required className="pr-10" data-testid="new-user-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Display Name *</Label>
                <Input placeholder="e.g., Dominos Manager" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="new-user-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Role *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Mall Staff</SelectItem>
                    <SelectItem value="brand">Brand</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.role === "brand" && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Brand Name *</Label>
                  <Input placeholder="e.g., Dominos" value={formData.brand_name} onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })} required data-testid="new-user-brand" />
                  <p className="text-xs text-[#94A3B8]">Must match exactly with voucher brand names</p>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-[#002FA7] hover:bg-[#001F6E]" data-testid="submit-new-user">Create User</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-[#E2E8F0]">
        <CardHeader className="border-b border-[#E2E8F0] pb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <UserCircleGear size={24} className="text-[#002FA7]" />
            <span>All Users</span>
            <span className="text-sm font-normal text-[#64748B] ml-auto">{users.length} users</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? <div className="p-8 text-center text-[#64748B]">Loading...</div> : users.length === 0 ? <div className="p-8 text-center text-[#64748B]">No users found.</div> : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-[#F8FAFC]">
                    <TableHead>Username</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="table-row-hover" data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-mono text-sm font-medium">{u.username}</TableCell>
                      <TableCell>{u.name}</TableCell>
                      <TableCell><Badge className={roleBadge[u.role] || roleBadge.staff}>{u.role}</Badge></TableCell>
                      <TableCell>{u.brand_name || "-"}</TableCell>
                      <TableCell><Badge className={u.is_active !== false ? "status-available" : "status-expired"}>{u.is_active !== false ? "Active" : "Inactive"}</Badge></TableCell>
                      <TableCell className="font-mono text-sm text-[#64748B]">{u.created_at ? format(new Date(u.created_at), "MMM dd, yyyy") : "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-[#002FA7]" data-testid={`edit-user-${u.id}`}><PencilSimple size={16} /></Button>
                          {u.role !== "admin" && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-user-${u.id}`}><Trash size={16} /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Edit User - {editUser?.username}</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Display Name</Label>
              <Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} data-testid="edit-user-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">New Password (leave blank to keep)</Label>
              <Input type="password" placeholder="Enter new password" value={editData.password} onChange={(e) => setEditData({ ...editData, password: e.target.value })} data-testid="edit-user-password" />
            </div>
            {editUser?.role === "brand" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Brand Name</Label>
                <Input value={editData.brand_name} onChange={(e) => setEditData({ ...editData, brand_name: e.target.value })} data-testid="edit-user-brand" />
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 bg-[#002FA7] hover:bg-[#001F6E]" data-testid="submit-edit-user">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
